import React, {useCallback, useMemo} from 'react';

import {FilterInput} from 'src/components/filter-input';
import {NoResultPage} from 'src/components/no-result-page/no-result-page';
import {DEBOUNCE_MS} from 'src/constants';

import {BoxLoading} from '@pb/kit-component-box-loading';
import {FlatButton} from '@pb/kit-component-button';
import Group from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import {Message} from '@pb/kit-component-message';
import Typography from '@pb/kit-component-typography';

import {useSupportedUserAuditActions} from '../hooks/use-supported-user-audit-actions';
import {useUserAuditLog} from '../hooks/use-user-audit-log';
import {
    auditLogHeader,
    auditLogSearch,
    auditLogSeparator,
    auditLogTotalCount,
} from '../user-profile.module.styl';

import {AuditLogTable} from './audit-log-table';

type Props = {
    userId: number,
};

export const AuditLogTab: React.FC<Props> = ({userId}) => {
    const {
        entries,
        totalCount,
        loading,
        error,
        page,
        pageSize,
        searchQuery,
        actionType,
        onPageChange,
        onPageSizeChange,
        onSearchQueryChange,
        onActionTypeChange,
    } = useUserAuditLog(userId);

    const {actionOptions, getTitleById} = useSupportedUserAuditActions(userId);

    const handleSearchClear = useCallback(() => {
        onSearchQueryChange('');
    }, [onSearchQueryChange]);

    const hasActiveCriteria = useMemo(
        () => Boolean(actionType) || Boolean(searchQuery),
        [actionType, searchQuery],
    );

    const handleClearCriteria = useCallback(() => {
        onSearchQueryChange('');
        onActionTypeChange(undefined);
    }, [onSearchQueryChange, onActionTypeChange]);

    const noResultMessage = useMemo(() => {
        if (hasActiveCriteria && !entries.length) {
            return (
                <NoResultPage
                    title="No data found for the selected criteria."
                    description=""
                />
            );
        } else if (!hasActiveCriteria && !entries.length) {
            return (
                <NoResultPage
                    title="No data to show."
                    description=""
                />
            );
        }

        return null;
    }, [hasActiveCriteria, entries]);

    return (
        <Group direction="v" gap="m">
            <header className={auditLogHeader}>
                <Group direction="h" alignItems="center">
                    <Typography className="font-weight-semi-bold" variant="heading-level-4">
                        Audit Log
                    </Typography>
                    {totalCount > 0 ? (
                        <>
                            <span className={auditLogSeparator}/>
                            <span className={auditLogTotalCount}>
                                {totalCount} {totalCount === 1 ? 'Action' : 'Actions'}
                            </span>
                        </>
                    ) : null}
                </Group>
            </header>
            {!(!totalCount && !hasActiveCriteria) ? (
                <Group direction="h" alignItems="center" gap="s">
                    <div className={auditLogSearch}>
                        <FilterInput
                            id="audit-log-search"
                            value={searchQuery}
                            onChange={onSearchQueryChange}
                            onClear={handleSearchClear}
                            timeout={DEBOUNCE_MS}
                            placeholder="Search for actions by description"
                        />
                    </div>
                    {hasActiveCriteria ? (
                        <FlatButton
                            startIcon={<Icon type={Icons.TRASH}/>}
                            onClick={handleClearCriteria}>
                            Clear criteria
                        </FlatButton>
                    ) : null}
                </Group>
            ) : null}

            {error != null ? (
                <Message color="error">{error}</Message>
            ) : null}

            <BoxLoading loading={loading}>
                {entries.length ? (
                    <AuditLogTable
                        entries={entries}
                        totalCount={totalCount}
                        loading={loading}
                        page={page}
                        pageSize={pageSize}
                        searchQuery={searchQuery}
                        actionType={actionType}
                        actionOptions={actionOptions}
                        getTitleById={getTitleById}
                        onPageChange={onPageChange}
                        onPageSizeChange={onPageSizeChange}
                        onActionTypeChange={onActionTypeChange}
                    />
                ) : noResultMessage}
            </BoxLoading>
        </Group>
    );
};
